import { memo, useMemo } from 'react'
import {
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
  Grid,
  useTheme
} from '@mui/material'

import Icon from '../../../../../../components/icon'
import RadarChart from '../../../../../../components/charts/radar'

import { getCefrScoreColor } from '../../../../../../utils/utils'
import { CEFR_RESULT_STATUS_LABEL } from '../../../../../../utils/constant'

export const CefrSectionTitle = memo(({ icon, title }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      mb: '14px',
      pageBreakInside: 'avoid'
    }}
  >
    <Icon
      icon={icon}
      style={{ fontSize: '20px', color: 'var(--primary-main)' }}
    />
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 700,
        color: 'var(--text-primary)',
        textTransform: 'uppercase'
      }}
    >
      {title}
    </Typography>
  </Box>
))

export const HiringSignalCard = memo(({ signal, forPdf = false }) => {
  const theme = useTheme()
  const color = getCefrScoreColor(signal?.score, theme?.palette)

  return (
    <Box
      sx={{
        p: '16px',
        borderRadius: '8px',
        bgcolor: 'var(--background-paper)',
        border: '1px solid var(--divider)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        height: forPdf ? 'auto' : '100%',
        pageBreakInside: 'avoid'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            color: 'var(--text-primary)'
          }}
        >
          {signal?.label}
        </Typography>
        {signal?.skills?.length > 0 && (
          <Typography sx={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            {signal?.skills?.join(' · ')}
          </Typography>
        )}
      </Box>

      {/* Progress bar */}
      <Box
        sx={{
          width: '100%',
          height: '6px',
          borderRadius: '3px',
          bgcolor: `${color}15`,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${signal?.score ?? 0}%`,
            bgcolor: color,
            borderRadius: '3px',
            transition: 'width 0.6s ease'
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 800,
            color: color
          }}
        >
          {signal?.score ?? 0}%
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: color
          }}
        >
          {CEFR_RESULT_STATUS_LABEL[signal?.resultStatus]}
        </Typography>
      </Box>
    </Box>
  )
})

export const SkillFeatureCard = memo(
  ({ skill, forPdf = false, showDescription = true }) => {
    const theme = useTheme()

    const color = useMemo(
      () => getCefrScoreColor(skill?.fitScore, theme?.palette),
      [theme?.palette, skill?.fitScore]
    )

    return (
      <Box
        sx={{
          p: '16px',
          borderRadius: '8px',
          border: '1px solid var(--divider)',
          bgcolor: 'var(--background-paper)',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          height: forPdf ? 'auto' : '100%',
          pageBreakInside: 'avoid'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 700,
                color: 'var(--text-primary)'
              }}
            >
              {skill?.cefrSkillLabel || ''}
            </Typography>
            <Typography
              sx={{
                fontSize: '11px',
                color: 'var(--text-secondary)',
                mt: '6px'
              }}
            >
              Weight: {skill?.weight ?? 0}%
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '2px',
                justifyContent: 'flex-end'
              }}
            >
              <Typography
                sx={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: color,
                  lineHeight: 1
                }}
              >
                {skill?.fitScore ?? 0}
              </Typography>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  fontWeight: 600
                }}
              >
                / 100
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '10px',
                color: 'var(--text-secondary)',
                mt: '6px'
              }}
            >
              {skill?.earnedMarks ?? 0}/{skill?.maxMarks ?? 0} marks
            </Typography>
          </Box>
        </Box>

        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={skill?.fitScore ?? 0}
          sx={{
            height: '6px',
            borderRadius: '3px',
            backgroundColor: `${color}20`,
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
              borderRadius: '3px'
            }
          }}
        />

        {skill?.description && showDescription && (
          <Typography
            sx={{
              fontSize: '11px',
              color: 'var(--text-secondary)',
              lineHeight: 1.5
            }}
          >
            {skill?.description}
          </Typography>
        )}
      </Box>
    )
  }
)

export const OverallScoreCard = memo(({ data, primaryColor }) => {
  const {
    overallFitScore,
    resultStatus,
    questionCount,
    attemptedQuestions,
    targetLevel,
    summary
  } = data || {}
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: '24px',
        p: '24px 32px',
        borderRadius: '12px',
        background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}08 100%)`,
        border: `1.5px solid ${primaryColor}40`,
        pageBreakInside: 'avoid'
      }}
    >
      {/* Circle Score */}
      <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          sx={{ color: `${primaryColor}20` }}
          size={80}
          thickness={2}
        />
        <CircularProgress
          variant="determinate"
          value={overallFitScore ?? 0}
          sx={{
            color: primaryColor,
            position: 'absolute',
            left: 0,
            strokeLinecap: 'round'
          }}
          size={80}
          thickness={2}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 700,
              color: primaryColor,
              lineHeight: 1
            }}
          >
            {overallFitScore ?? 0}
          </Typography>
          <Typography
            sx={{
              fontSize: '10px',
              color: 'var(--text-secondary)',
              fontWeight: 600,
              mt: '4px'
            }}
          >
            / 100
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}
        >
          <Box
            sx={{
              bgcolor: `${primaryColor}`,
              px: 2,
              py: 1,
              borderRadius: '16px',
              display: 'flex'
            }}
          >
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 700,
                color: '#fff'
              }}
            >
              {targetLevel ? `${targetLevel} · ` : ''}
              {CEFR_RESULT_STATUS_LABEL[resultStatus]}
            </Typography>
          </Box>
          {questionCount != null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'var(--grey-100)',
                px: 1.5,
                py: 0.5,
                borderRadius: '6px'
              }}
            >
              <Typography
                fontSize="12px"
                color="var(--grey-600)"
                fontWeight={600}
              >
                {attemptedQuestions != null ? (
                  <>
                    Attempted:{' '}
                    <Typography
                      component="span"
                      fontSize="12px"
                      fontWeight={800}
                      color="var(--grey-800)"
                    >
                      {attemptedQuestions}/{questionCount}
                    </Typography>{' '}
                    Questions
                  </>
                ) : (
                  `${questionCount} questions`
                )}
              </Typography>
            </Box>
          )}
        </Box>
        {summary && (
          <Typography
            sx={{
              fontSize: '14px',
              color: 'var(--text-primary)',
              lineHeight: 1.5
            }}
          >
            {summary}
          </Typography>
        )}
      </Box>
    </Box>
  )
})

export const CEFRCommunicateReport = memo(
  ({ cefrReport, forPdf = false, showOverallScoreCard = true }) => {
    const theme = useTheme()
    const primaryColor = useMemo(
      () => theme.palette.primary.main,
      [theme.palette.primary.main]
    )

    const radarLabels = useMemo(
      () => (cefrReport?.skills || []).map((s) => s.cefrSkillLabel),
      [cefrReport]
    )
    const radarValues = useMemo(
      () => (cefrReport?.skills || []).map((s) => s.fitScore),
      [cefrReport]
    )

    if (!cefrReport) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 6,
            bgcolor: 'var(--background-paper)',
            borderRadius: '12px',
            border: '1px dashed var(--divider)',
            minHeight: '300px'
          }}
        >
          <Icon
            icon="mdi:file-document-remove-outline"
            style={{ fontSize: 64, color: 'var(--text-disabled)' }}
          />
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: 'var(--text-secondary)'
            }}
          >
            CEFR Report Not Available
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* 1. Overall Assessment */}
        {showOverallScoreCard && (
          <Box>
            <OverallScoreCard data={cefrReport} primaryColor={primaryColor} />
          </Box>
        )}

        {/* 2. Skill Radar Chart */}
        {radarLabels?.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Box sx={{ alignSelf: 'flex-start' }}>
              <CefrSectionTitle icon="mdi:radar" title="Performance Radar" />
            </Box>
            <Box
              sx={{
                height: 300,
                width: '100%',
                maxWidth: 500,
                pageBreakInside: 'avoid'
              }}
            >
              <RadarChart
                indicators={radarLabels}
                values={radarValues}
                max={100}
                showFullLengthTitle
              />
            </Box>
          </Box>
        )}

        {/* 3. Skill Breakdown */}
        <Box>
          <CefrSectionTitle
            icon="mdi:format-list-checks"
            title="Skill-wise Breakdown"
          />
          <Grid container spacing={4}>
            {(cefrReport?.skills || [])?.map((skill) => (
              <Grid item xs={12} md={6} key={skill?.cefrSkill}>
                <SkillFeatureCard skill={skill} forPdf={forPdf} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* 4. Hiring Signals */}
        {cefrReport?.hiringSignals?.length > 0 && (
          <Box sx={{ pageBreakInside: 'avoid' }}>
            <CefrSectionTitle
              icon="mdi:briefcase-check-outline"
              title="Hiring Signals"
            />
            <Grid container spacing={4}>
              {cefrReport?.hiringSignals?.map((signal) => (
                <Grid item xs={12} sm={6} md={3} key={signal?.label}>
                  <HiringSignalCard signal={signal} forPdf={forPdf} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    )
  }
)
